import React, { useEffect, useState } from "react";
import { X, Download } from "lucide-react";

/**
 * Small dismissable prompt that surfaces the native "Add to Home Screen"
 * install dialog on Chrome/Edge/Android, and provides iOS Safari users with
 * inline instructions (Apple does not expose beforeinstallprompt).
 */
export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("njas-install-dismissed") === "1") return;
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (isStandalone) return;

    const ua = window.navigator.userAgent || "";
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari: show hint after a small delay when the flag is not fired
    let iosTimer = null;
    if (isIos) {
      iosTimer = setTimeout(() => { setShowIosHint(true); setVisible(true); }, 4000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
    localStorage.setItem("njas-install-dismissed", "1");
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem("njas-install-dismissed", "1");
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed z-40 bottom-20 md:bottom-4 left-1/2 -translate-x-1/2 w-[92%] max-w-md rounded-2xl border border-[var(--njas-border)] bg-white shadow-xl p-4 flex items-start gap-3"
    >
      <div className="w-10 h-10 rounded-lg bg-njas-navy text-white flex items-center justify-center shrink-0">
        <Download className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-njas-navy">Install NJAS Portal</div>
        {showIosHint ? (
          <div className="text-xs text-njas-muted mt-0.5">
            Tap <strong>Share</strong> then <strong>Add to Home Screen</strong> to install.
          </div>
        ) : (
          <div className="text-xs text-njas-muted mt-0.5">Get one-tap access from your home screen. Works offline.</div>
        )}
        {!showIosHint && deferred && (
          <button onClick={install} data-testid="pwa-install-btn"
            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-njas-navy text-white text-xs px-3 py-1.5 hover:bg-njas-navy-hover">
            Install now
          </button>
        )}
      </div>
      <button onClick={dismiss} data-testid="pwa-install-dismiss" className="text-njas-muted hover:text-njas-navy">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
